import styled, { css } from 'styled-components';

import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CiClock2 as ClockIcon } from 'react-icons/ci';
import { makeCheckoutLink } from '../../../firebase/stripe.js';
import { TYPE_PAYMENT } from '../../../helper/type-payment.js';
import useBookVenue from '../../../hooks/useBookVenue';
import { setShowSignIn } from '../../../store/user.js';
import { useClickBookNowTag } from '../../../tag-manager/booking.tag.js';
import { companyEmail, getBookingDetails } from '../../helpers';
import {
  CalendarIcon,
  capitalize,
  CloseIcon,
  colors,
  CreditCardIcon,
  headingStyles,
  HostIcon,
  IndoorOutdoorIcon,
  LocationIcon,
  mobile,
  notMobile,
  PeopleIcon,
  tablet,
  VenueIcon
} from '../../styles';
import AmenityIcon from './AmenityIcon';
import Photos from './Photos';
import RefundPolicy from './RefundPolicy';
import VenueFacts from './VenueFacts';
import { hasNewFacts, priceLine, valuesOf } from './venueFactsView';
import VenueText from '../../Shared/VenueText';

const Background = styled.div`
  color: ${colors.defaultText};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  min-height: 115px;

  @media (max-width: ${tablet}) {
    min-height: 100px;
  }

  @media (max-width: ${mobile}) {
    min-height: 0;
  }
`;

const TitleContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  ${headingStyles}
  font-size: 32px;
  line-height: 1.2;

  @media (max-width: ${tablet}) {
    font-size: 24px;
  }

  @media (max-width: ${mobile}) {
    font-size: 22px;
  }
`;

const Location = styled.div`
  font-size: 14px;
  line-height: 1.2;
  margin-top: 12px;
  flex-grow: 1; // position attributes to the bottom

  @media (max-width: ${tablet}) {
    font-size: 12px;
  }

  svg {
    display: inline-block;
    height: 18px;
    width: 18px;
    margin-right: 2px;
    margin-top: -2px;
    fill: ${colors.primaryIcon};
  }
`;

const Attributes = styled.div`
  display: flex;
  font-size: 14px;
  margin-top: 15px;

  @media (max-width: ${tablet}) {
    font-size: 12px;
    flex-wrap: wrap;
    height: 18px;
    overflow: hidden;
  }

  div {
    flex: 0 0 auto;
    text-align: left;
  }
  div + div {
    padding-left: 22px;

    @media (max-width: ${tablet}) {
      padding-left: 16px;
    }
  }

  svg {
    display: inline-block;
    width: 18px;
    height: 18px;
    margin-right: 2px;
    margin-top: -4px;

    @media (max-width: ${tablet}) {
      width: 14px;
      height: 14px;
      margin-top: -3px;
    }
  }
`;

const CloseButtonWrapper = styled.div`
  flex: 0 0 auto;

  @media (min-width: ${notMobile}) {
    display: none;
  }

  button {
    border: none;
    background-color: transparent;
    padding: 6px 2px;
    margin: 0;

    svg {
      height: 18px;
      width: 18px;
    }
  }
`;

const BookContent = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;

  @media (max-width: ${mobile}) {
    display: none;
  }
`;

const MobileBookContent = styled.div`
  display: none;

  @media (max-width: ${mobile}) {
    display: flex;
    padding-bottom: 20px;
    margin-bottom: 16px;
    border-bottom: 1px solid ${colors.lightBorder};
    align-items: center;

    div:first-child {
      flex-grow: 1;
      text-align: left;
    }
  }
`;

const BookButton = styled.button`
  font-weight: 500;
  color: white;
  background-color: ${colors.primaryButton};
  font-size: 14px;
  border-radius: 4px;
  padding: 15px 50px;
  border: none;

  @media (max-width: ${tablet}) {
    padding: 12px 33px;
  }

  &:hover {
    opacity: 0.9;
  }
`;

const ChargeText = styled.div`
  color: ${colors.fadedText};
  text-align: center;
  margin-top: 10px;
  flex-grow: 1;
`;

const PriceText = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  text-align: right;

  span {
    font-size: 12px;
  }

  @media (max-width: ${mobile}) {
    font-size: 18px;

    span {
      font-size: 14px;
    }
  }
`;

const DescriptionText = styled.div`
  font-size: 14px;
  line-height: 22px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${colors.lightBorder};
  margin-bottom: 32px;

  @media (max-width: ${tablet}) {
    padding-bottom: 24px;
    margin-bottom: 24px;
  }

  @media (max-width: ${mobile}) {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const SubTitle = styled.div`
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  margin-bottom: 16px;
`;

const GenericDetails = styled.div`
  margin-bottom: 32px;

  div:nth-child(2) {
    font-size: 14px;
    line-height: normal;

    svg {
      display: inline-block;
      width: 18px;
      height: 18px;
      margin-top: -2px;
      margin-right: 2px;
    }
  }
`;

const AmenitiesDetails = styled.div`
  margin-bottom: 32px;

  & > div:nth-child(2) {
    display: flex;
    flex-wrap: wrap;

    div {
      flex: 0 0 25%;
      font-size: 12px;
      margin-bottom: 10px;
      padding-right: 10px;

      @media (max-width: 1300px) {
        flex-basis: 33.3%;
      }

      @media (max-width: ${tablet}) {
        flex-basis: 50%;
      }

      svg {
        display: inline-block;
        height: 14px;
        width: 14px;
        margin-top: -3px;
      }
    }
  }
`;

const BookingDetails = styled.div`
  margin-bottom: 32px;
  padding: 16px;
  background-color: ${colors.primaryBackground};
  border-radius: 6px;
  font-size: 14px;

  ${({ isCancelled }) =>
    isCancelled &&
    css`
      background-color: ${colors.secondaryBackground};
    `}

  @media (max-width: ${tablet}) {
    font-size: 12px;
  }

  & > div {
    display: flex;
  }

  & > div:first-child {
    font-weight: 500;
    line-height: 22px;

    & > div:first-child {
      flex-grow: 1;
    }
  }

  & > div:not(:first-child) {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px; // match gap value
    gap: 10px;

    div {
      flex: 0 0 auto;
      display: flex;
      text-align: left;
      padding-right: 24px;
    }

    a {
      border: none;
      color: ${colors.defaultText};
      text-decoration: underline;
    }
  }

  svg {
    display: inline-block;
    width: 18px;
    height: 18px;
    margin-right: 2px;
    margin-top: -4px;

    @media (max-width: ${tablet}) {
      width: 14px;
      height: 14px;
      margin-top: -3px;
    }
  }
`;

const DetailView = ({ venue, bookNow, close }) => {
  // "Pay remaining" when signed out opens sign-in; `dispatch` was never defined here.
  const dispatch = useDispatch();
  const locationPath = useLocation();

  const isHostReservation = locationPath.pathname.includes('/host/reservations');

  const onCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const onBookClick = useBookVenue(venue);
  const handleBookingClick = (e) => {
    if (onBookClick) {
      onBookClick(e);
    }
    useClickBookNowTag({ label: 'Book Now Detail' });
  };

  if (!venue) {
    return null;
  }

  // Venue-search VS1.5: the new facts, and the day price with its hours.
  const facts = valuesOf(venue);
  const newFacts = hasNewFacts(facts);
  const price = priceLine(venue);

  const {
    isBooking,
    isCancelled,
    bookingName,
    bookingEmail,
    location,
    dateLabel,
    timeText,
    bookingDateLabel,
    isReserved,
    typeBooking,
    paymentsFormat
  } = getBookingDetails(venue);

  // Billing B4.3: no "change these times" note after the event. Dates are 'YYYY-MM-DD'.
  const eventIsPast = !!venue.booking && venue.booking.end < new Date().toLocaleDateString('en-CA');

  const onSecondPaymentClick = async () => {
    if (!venue.booking) {
      console.log('No booking found for second payment');
    }

    // Billing B2.1: the server reads the dates and the price from the stored booking.
    const { url, error, unauthenticated, message } = await makeCheckoutLink({
      venue: { id: venue.booking.venue_id },
      typePayment: TYPE_PAYMENT.PARTIAL_PAYMENT,
      booking_id: venue.booking.stripe_id
    });

    if (unauthenticated) {
      dispatch(setShowSignIn());
      return;
    }

    if (error) {
      alert(message || 'The payment could not be completed. Please try again.'); // o setError si tienes un estado
      return;
    }

    window.location.href = url;
  };

  return (
    <Background>
      <HeaderContent>
        <TitleContent>
          <Title>{venue.name}</Title>
          <Location>
            <LocationIcon />
            {location}
          </Location>
          <Attributes>
            <div>
              <PeopleIcon />
              {venue.maxGuests} Guests
            </div>
            <div>
              <IndoorOutdoorIcon />
              {capitalize(venue.setting)}
            </div>
            <div>
              <VenueIcon />
              {capitalize(venue.venueType)}
            </div>
          </Attributes>
        </TitleContent>
        <CloseButtonWrapper>
          <button onClick={onCloseClick}>
            <CloseIcon />
          </button>
        </CloseButtonWrapper>
        {!!bookNow && (
          <BookContent>
            <BookButton onClick={handleBookingClick}>Book Now</BookButton>
            <ChargeText>You won't be charged yet</ChargeText>
            <PriceText>
              {price.price}
              <span>{price.hours}</span>
            </PriceText>
          </BookContent>
        )}

        {isBooking && isReserved && typeBooking === TYPE_PAYMENT.PARTIAL_PAYMENT && !isHostReservation && (
          <BookContent>
            <BookButton onClick={onSecondPaymentClick}>Pay the remaining amount</BookButton>
          </BookContent>
        )}
      </HeaderContent>
      <Photos venue={venue} />
      {!!bookNow && (
        <MobileBookContent>
          <PriceText>
            {price.price}
            <span>{price.hours}</span>
          </PriceText>
          <BookButton onClick={handleBookingClick}>Book Now</BookButton>
        </MobileBookContent>
      )}

      {isBooking && isReserved && typeBooking === TYPE_PAYMENT.PARTIAL_PAYMENT && !isHostReservation && (
        <MobileBookContent>
          <BookButton onClick={onSecondPaymentClick}>Pay the remaining amount</BookButton>
        </MobileBookContent>
      )}

      {isBooking && (
        <BookingDetails isCancelled={isCancelled}>
          <div>
            <div>Reservation Details</div>
          </div>
          <div>
            <CalendarIcon /> {dateLabel}
          </div>
          {/* Billing B4.3: event times (Braeden, 2026-10-31). The booker sees how to change them. */}
          {!!timeText && (
            <div>
              <span>
                <ClockIcon /> {timeText}
              </span>
            </div>
          )}
          {!!timeText && !isHostReservation && !isCancelled && !eventIsPast && (
            <div>
              <span>
                To change these times, email <a href={`mailto:${companyEmail}`}>{companyEmail}</a>. We can change them if the host agrees.
              </span>
            </div>
          )}
          <div>
            {paymentsFormat.map((paymentFormat, index) => (
              <div key={index}>
                <CreditCardIcon />
                <span>{paymentFormat}</span>
              </div>
            ))}
          </div>
          <div>
            <div>
              <HostIcon />
              {bookingName}, <a href={`mailto:${bookingEmail}`}>{bookingEmail}</a>
            </div>
          </div>
          {/* Billing B6.2 (D-0056): no self-cancel. Cancels go to support. */}
          {!isCancelled && (
            <div>
              <span>
                To cancel, email <a href={`mailto:${companyEmail}`}>{companyEmail}</a>.
              </span>
            </div>
          )}
        </BookingDetails>
      )}
      <DescriptionText>
        <VenueText text={venue.description} />
      </DescriptionText>
      {newFacts && <VenueFacts venue={venue} values={facts} />}
      {/* VS1.5 decision A: the old Amenities list only for venues without the new facts. */}
      {!newFacts && Array.isArray(venue.amenities) && venue.amenities.length > 0 && (
        <AmenitiesDetails>
          <SubTitle>Amenities</SubTitle>
          <div>
            {venue.amenities.map((item) => (
              <div key={item}>
                <AmenityIcon type={item} />
                {item}
              </div>
            ))}
          </div>
        </AmenitiesDetails>
      )}
      {!!venue.additionalNotes && (
        <GenericDetails>
          <SubTitle>Additional notes from your host</SubTitle>
          <div>
            <VenueText text={venue.additionalNotes} />
          </div>
        </GenericDetails>
      )}
      <RefundPolicy splitPayment={!!venue.allowPayAnotherAmount} />
    </Background>
  );
};

export default DetailView;
